// PropertyValuationDashboard.tsx
import React, { useState, useEffect } from "react";
import {
  DollarSign,
  Home,
  MapPin,
  Ruler,
  Building,
  TrendingUp,
  Shield,
  Clock,
  Zap,
  CheckCircle,
  AlertCircle,
  Maximize2,
  Download,
} from "lucide-react";

import DonutChart from "./DonutChart";
import SelectedLocation from "../supervisor/PropertyLocation";

interface PropertyValuationData {
  openMarketValue: number;
  insuranceValue: number;
  forcedSaleValue: number;
  improvementValue: number;
  landArea: number; // in m²
  landValue: number;
  buildingValue: number;
  location: {
    plotNumber: string;
    village: string;
    cell: string;
    sector: string;
    district: string;
    city: string;
  };
  propertyType: string;
  constructionDetails: {
    bedrooms: number;
    bathrooms: number;
    totalRooms: number;
    floorArea: number; // in m²
    yearOfConstruction: number;
    yearBuilt?: number;
  };
  amenities: string[];
  zoning: string;
  valuationDate: string;
  lastUpdated: string;
  comparables?: {
    property: string;
    distance: number;
    value: number;
    date: string;
  }[];
  marketTrends?: {
    period: string;
    averagePrice: number;
    avaragePriceN?: string;
    change: number;
  }[];
}

interface ValuationSegment {
  label: string;
  value: number;
  color: string;
  percentage: number;
}

interface Property {
  id: string;
  upi?: string;
  ownerName: string;
  ownerIdNumber: string;
  phone?: string;
  plotNumber?: string;
  location: Location;
  landParcel?: LandParcel;
  mainBuilding?: Building;
  amenities?: Amenities;
  buildingComponents?: BuildingComponents;
  valuationDetails?: ValuationDetails;
  adjustmentsAndVariables: any;
  buildingDescription: any;
  propertyDetails: any;
  propertyPhotos: any;
  verandahRate?: number;
  verandahArea?: number;
  // Image fields
  frontView?: string;
  backView?: string;
  leftView?: string;
  rightView?: string;
  livingRoom?: string;
  kitchen?: string;
  bathroom?: string;
  bedroom?: string;
  diningRoom?: string;
  veranda?: string;
  googleEarthOne?: string;
  googleEarthTwo?: string;
  masterPlanPhoto?: string;
  photo?: string;
}

interface DonutSegment {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
}

const PropertyValuationDashboard: React.FC = (property: Property) => {
  const amenities: string[] = [];
  
  if (property.property?.amenitiesData.hasElectricity) amenities.push("Electricity");
  if (property.property?.amenitiesData.hasWater) amenities.push("Water");
  if (property.property?.amenitiesData.accessToRoad) amenities.push("Access To road");
  if (property.property?.amenitiesData.hasPerimeterFence) amenities.push("Perimeter Fence");
  if (property.property?.amenitiesData.hasPavedParking) amenities.push("Parking Yard");
  if (property.property?.amenitiesData.hasSepticTank) amenities.push("Septic Tank");
  if (property.property?.amenitiesData.hasGarden) amenities.push("Garden");
  if (property.property?.amenitiesData.hasOutsideKitchen) amenities.push("Outside Kitchen");
  if (property.property?.amenitiesData.hasAnnexBuilding) amenities.push("Annex Building");

  const mainBuildingValue =
    property.property?.valuationDetails?.mainHouseRate *
    property.property?.valuationDetails?.mainHouseArea;
  const annexValue =
    property.property?.valuationDetails?.annexRate *
    property.property?.valuationDetails?.annexHouseArea;
  const siteworksValue =
    property.property?.valuationDetails?.fenceRate *
      property.property?.valuationDetails?.fenceLength +
    property.property?.valuationDetails?.retainingWallRate *
      property.property?.valuationDetails?.retainingWallsLength +
    property.property?.valuationDetails?.entranceGateRate *
      property.property?.valuationDetails?.entranceGateLength +
    property.property?.valuationDetails?.pavedAreaRate *
      property.property?.valuationDetails?.pavedArea +
    property.property?.valuationDetails?.septicTankValue *
      property.property?.valuationDetails?.septicTankNumber +
    property.property?.valuationDetails?.gardenRate *
      property.property?.valuationDetails?.gardenArea +
    property.property?.valuationDetails?.verandahRate *
      property.property?.valuationDetails?.verandahArea;

  const [valuationData, setValuationData] = useState<PropertyValuationData>({
    openMarketValue:
      property?.property?.valuationDetails?.openMarketValue ,
    insuranceValue:
      property?.property?.valuationDetails?.insuranceValue ,
    forcedSaleValue:
      property?.property?.valuationDetails?.forcedSaleValue ,
    improvementValue: property?.property?.valuationDetails?.improvementsValue ,
    landArea: property?.property?.landArea || 342,
    landValue: property?.property?.valuationDetails?.landValue ,
    buildingValue:
      property.property?.valuationDetails?.mainHouseRate *
        property.property?.valuationDetails?.mainHouseArea ,
    location: {
      plotNumber: "3903",
      village: "RUBUNGO",
      cell: "KINYAGA",
      sector: "BUMBOGO",
      district: "Gasabo",
      city: "Kigali",
    },
    propertyType: "Residential House",
    constructionDetails: {
      bedrooms: property.property?.mainBuilding.bedrooms,
      bathrooms: property.property?.mainBuilding.bathrooms,
      totalRooms: 10,
      floorArea: property.property?.valuationDetails?.mainHouseArea ,
      yearOfConstruction: property.property?.yearOfConstruction ,
    },
    amenities,
    zoning: property.property?.landUse,
    valuationDate: "20225-01-31",
    lastUpdated: "2025-01-15",
    comparables: [
      {
        property: "Plot 3251",
        distance: 50,
        value: 82000000,
        date: "2021-11-15",
      },
      {
        property: "Plot 2909",
        distance: 75,
        value: 89000000,
        date: "2022-12-20",
      },
      {
        property: "Plot 3002",
        distance: 120,
        value: 78000000,
        date: "2023-01-05",
      },
      {
        property: "Plot 3002",
        distance: 120,
        value: 78000000,
        date: "2025-01-05",
      },
    ],
    marketTrends: [
      { period: "2022", averagePriceN: "N/A", change: 0 },
      { period: "2023", averagePriceN: "N/A", change: 0 },
      { period: "2024", averagePriceN: "N/A", change: 0 },
      { period: "2025", averagePriceN: "N/A", change: 0 },
      {
        period: "2026",
        averagePrice:
          property?.property?.valuationDetails?.openMarketValue || 86000000,
        change: 0,
      },
    ],
  });

  const [activeView, setActiveView] = useState<
    "overview" | "breakdown" | "timeline" | "comparables"
  >("overview");
  const [timeframe, setTimeframe] = useState<"1y" | "3y" | "5y">("3y");

  // ── EDIT 1: Currency detection based on property country ──────────────
  const isUganda =
    !!property?.property?.location?.region ||
    property?.property?.location?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";

  const formatCurrency = (amount: number): string =>
    `${currency} ${Number(amount || 0).toLocaleString()}`;
  // ─────────────────────────────────────────────────────────────────────

  // Calculate valuation segments for donut chart
  const getValuationSegments = (): ValuationSegment[] => {
    const segmentsBase = [
      {
        label: "Land Value",
        value: valuationData.landValue,
        color: "#10B981", // emerald
      },
      {
        label: "Main Building",
        value: mainBuildingValue,
        color: "#3B82F6", // blue
      },
      {
        label: "Annex & Structures",
        value: annexValue,
        color: "#F59E0B", // amber
      },
      {
        label: "Site Works",
        value: siteworksValue,
        color: "#8B5CF6", // purple
      },
    ];

    const total = segmentsBase.reduce((sum, s) => sum + s.value, 0);

    return segmentsBase.map((s) => ({
      ...s,
      percentage: (s.value / total) * 100,
    }));
  };

  const chartData: DonutSegment[] = [
    { label: "Land Value", value: 500000, percentage: 30, color: "#3B82F6" },
    { label: "Main Building", value: 800000, percentage: 50, color: "#10B981" },
    {
      label: "Annex & Structures",
      value: 200000,
      percentage: 12,
      color: "#F59E0B",
    },
    { label: "Site Works", value: 100000, percentage: 8, color: "#EF4444" },
  ];

  const segments = getValuationSegments();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 md:py-6">
      {/* Header */}
      <div className="mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Key Metrics */}
          <div className="lg:col-span-3 space-y-6">
            {/* Value Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-300 rounded-xl shadow-lg p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    Open Market Value
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valuationData.openMarketValue)}
                </h3>
                <p className="text-gray-600 text-sm">
                  Estimated amount for which the property should exchange
                  between a willing buyer and seller
                </p>
                <div className="mt-4 pt-4 ">
                  <div className="flex items-center text-green-600 text-sm">
                    <Zap className="w-4 h-4 mr-1" />
                    <span>+0% from last valuation</span>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl shadow-lg p-6 border border-emerald-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Shield className="w-6 h-6 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                    Insurance Value
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valuationData.insuranceValue)}
                </h3>
                <p className="text-gray-600 text-sm">
                  Value for insurance purposes, typically covering replacement
                  cost
                </p>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-gray-600 text-sm">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    <span>±5% margin of error</span>
                  </div>
                </div>
              </div>

              <div className="bg-rose-50 rounded-xl shadow-lg p-6 border border-amber-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <span className="text-sm font-medium text-red-600 bg-amber-50 px-3 py-1 rounded-full">
                    Forced Sale Value
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valuationData.forcedSaleValue)}
                </h3>
                <p className="text-gray-600 text-sm">
                  Estimated realization price in forced sale circumstances
                </p>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-red-600 text-sm">
                    <span>70% of market value</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-500 rounded-xl shadow-lg p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPin className="w-6 h-6 text-yellow-600" />
                  </div>
                  <span className="text-sm font-medium text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
                    Land Value
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valuationData.landValue)}
                </h3>
                <p className="text-gray-600 text-sm">
                  Value of the land based on area and location
                </p>
              </div>

              <div className="bg-orange-500 rounded-xl shadow-lg p-6 border border-emerald-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Building className="w-6 h-6 text-red-600" />
                  </div>
                  <span className="text-sm font-medium text-red-600 bg-emerald-50 px-3 py-1 rounded-full">
                    Building Replacement Cost
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valuationData.insuranceValue)}
                </h3>
                <p className="text-gray-600 text-sm">
                  Estimated the cost to replace a property as new
                </p>
              </div>

              <div className="bg-green-300 rounded-xl shadow-lg p-6 border border-amber-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Maximize2 className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-green-600 bg-amber-50 px-3 py-1 rounded-full">
                    Improvement Value
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valuationData.improvementValue)}
                </h3>
                <p className="text-gray-600 text-sm">
                  Total value of all buildings and improvements
                </p>
              </div>
            </div>

            {/* Valuation Breakdown */}
            <div className="grid gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Valuation Breakdown
                  </h2>
                  <div className="flex space-x-2">
                    {["overview", "breakdown", "timeline", "comparables"].map(
                      (view) => (
                        <button
                          key={view}
                          onClick={() => setActiveView(view as any)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            activeView === view
                              ? "bg-blue-600 text-white"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {view.charAt(0).toUpperCase() + view.slice(1)}
                        </button>
                      )
                    )}
                  </div>
                </div>

                {activeView === "overview" && (
                  <div className="space-y-6">
                    <div className="flex flex-col md:flex-row gap-8">
                      {/* Donut Chart Visualization */}
                      <div className="flex-1">
                        <div className="-mt-12 relative">
                          <DonutChart
                            segments={segments}
                            openMarketValue={valuationData.openMarketValue}
                            currency={currency}
                          />
                        </div>
                      </div>

                      {/* Segment Details */}
                      <div className="flex-1 space-y-4">
                        {segments.map((segment) => (
                          <div
                            key={segment.label}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: segment.color }}
                              />
                              <span className="font-medium text-gray-700">
                                {segment.label}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-gray-900">
                                {formatCurrency(segment.value)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeView === "breakdown" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-bold text-gray-700 mb-2">
                          Building Description
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Total Floor Area
                            </span>
                            <span className="font-medium">
                              {valuationData.constructionDetails.floorArea} m²
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Bedrooms</span>
                            <span className="font-medium">
                              {valuationData.constructionDetails.bedrooms}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Bathrooms</span>
                            <span className="font-medium">
                              {valuationData.constructionDetails.bathrooms}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Year Of Construction
                            </span>
                            <span className="font-medium">
                              {valuationData.constructionDetails.yearOfConstruction}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-bold text-gray-700 mb-2">
                          Land Details
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Plot Area</span>
                            <span className="font-medium">
                              {valuationData.landArea} m²
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Land Value per m²
                            </span>
                            {/* ── EDIT 2: replaced hardcoded Rwf ── */}
                            <span className="font-medium">
                              {currency}{" "}
                              {Math.round(
                                valuationData.landValue / valuationData.landArea
                              ).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Zoning</span>
                            <span className="font-medium">
                              {valuationData.zoning}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeView === "timeline" && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">
                      Open Market Value
                    </h2>
                    <div className="space-y-3">
                      {valuationData.marketTrends?.map((trend, index) => (
                        <div
                          key={trend.period}
                          className="flex items-center justify-between"
                        >
                          <span className="text-gray-700">{trend.period}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-medium">
                              {trend.averagePrice
                                ? formatCurrency(trend.averagePrice)
                                : trend.averagePriceN}
                            </span>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                trend.change > 0
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {trend.change > 0 ? "+" : ""}
                              {trend.change}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeView === "comparables" && (
                  <section>
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📊</span> Comparative Analysis
                    </h2>
                    <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Property
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Area
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Price
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Adjustments
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              Subject Property
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {property.location?.district},{" "}
                              {property.location?.sector}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {/* {property?.area || 0} m² */}
                            </td>
                            {/* ── EDIT 3: replaced hardcoded RWF ── */}
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {currency}{" "}
                              {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              N/A
                            </td>
                          </tr>
                          <tr className="bg-blue-50">
                            <td
                              className="px-4 py-3 text-sm font-medium text-blue-900"
                              colSpan={3}
                            >
                              Comparable Value
                            </td>
                            <td className="px-4 py-3 text-lg font-bold text-blue-900">
                              {currency}{" "}
                              {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-600">
                              Market Adjusted
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        Summary
                      </h3>
                      <p className="text-sm text-gray-600">
                        Based on property data, the comparable Value is
                        estimated at{" "}
                        <span className="font-semibold">
                          {currency}{" "}
                          {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                        </span>
                        .
                      </p>
                    </div>
                  </section>
                )}
              </div>

              {/* Right Column - Property Details */}
              <div className="space-y-6">
                {/* Property Card */}
                <div className="bg-white flex rounded-xl shadow-lg p-6">
                  <SelectedLocation location={property} />
                  <div className="space-y-4">
                    <div className="items-start gap-3 mx-20 mt-10">
                      <div className="flex">
                        <Building className="w-5 mr-2 h-5 text-gray-400 mt-0.5" />
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center justify-center">
                          Amenities
                        </h3>
                      </div>
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 p-2">
                          {valuationData.amenities.map((amenity) => (
                            <span
                              key={amenity}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                            >
                              <CheckCircle className="w-3 h-3" />
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer Status */}
      </div>
    </div>
  );
};

export default PropertyValuationDashboard;